import { IfAbility } from '@efdigital/ll-addon-auth';
import type { MediaImageSizeContent } from '@efdigital/ll-addon-data';
import { Heading } from '@efdigital/ll-addon-ui';
import { EditMediaImage } from '@efdigital/ll-admin-v2/components/media/edit-media-image.gts';
import { getMediaImage } from '@efdigital/ll-admin-v2/data/builders/content/media-images';
import {
  MediaImageSize,
  type Size,
} from '@efdigital/ll-admin-v2/lib/media/models';
import type { LibraryMediaImagesEditModel } from '@efdigital/ll-admin-v2/routes/library/media-images/edit';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

function sortSize(sizes: MediaImageSizeContent[]) {
  // sort by width
  return (sizes || []).sort((a, b) => a.width - b.width);
}

function halfSize(size: Size) {
  return {
    width: Math.round(size.width / 2),
    height: Math.round(size.height / 2),
  };
}

function isRetina(size: string) {
  switch (size) {
    case MediaImageSize.ResourceIcon:
    case MediaImageSize.CohortIcon:
    case MediaImageSize.ModuleIcon:
    case MediaImageSize.ContactIcon:
    case 'original':
      return true;
    default:
      return false;
  }
}

export default class LibraryMediaImagesEdit extends Component<{
  Args: {
    model: LibraryMediaImagesEditModel;
  };
}> {
  @service declare store: Store;
  @service declare router: RouterService;

  queryMediaImage = (id: string) => {
    return getMediaImage(id);
  };

  onSave = () => {
    this.router.transitionTo('library.media-images.index');
  };

  <template>
    <div class="container py-5">
      <Heading @text="Image Library" @secondary="Edit Image">
        <LinkTo
          @route="library.media-images.index"
          class="btn btn-sm btn-primary"
        >
          <iconify-icon inline icon="ph:arrow-left" />
          Back to Image Library
        </LinkTo>
      </Heading>
      <div class="row mb-5 g-5">
        <div class="col-xl-6 order-2 order-xl-1">
          <img
            src={{@model.mediaImage.original.url}}
            alt={{@model.mediaImage.alt}}
            class="img-fluid rounded shadow"
          />
        </div>
        <div class="col-xl-6 order-1 order-xl-2">
          <IfAbility @ability="library" @atLeast="editor">
            <:if>
              <EditMediaImage
                @mediaImage={{@model.mediaImage}}
                @onSave={{this.onSave}}
              />
            </:if>
            <:else>
              <div class="alert alert-info">
                <h4 class="alert-heading">View Only</h4>
                <p class="mb-0">You need the "Edit media library items"
                  permission to edit media.</p>
              </div>
            </:else>
          </IfAbility>
        </div>
      </div>

      <h3 class="gud-eyebrow">Previews</h3>
      <p>The image has been converted to the following sizes. If you need a
        different size, it will be generated automatically when you use the
        image in the relevant context.</p>
    </div>
    <div class="text-center">
      {{#each (sortSize @model.mediaImage.sizes) as |version|}}
        <div class="overflow-scroll">
          <figure>
            {{#let (isRetina version.size) as |retina|}}
              {{#let (if retina (halfSize version) version) as |size|}}
                <img
                  src={{version.url}}
                  alt="{{version.size}}"
                  class="figure-img"
                  width={{size.width}}
                  height={{size.height}}
                />
                <figcaption class="figure-caption">{{version.size}}
                  ({{size.width}}x{{size.height}}{{#if retina}}
                    @2X{{/if}})</figcaption>
              {{/let}}
            {{/let}}
          </figure>
        </div>
      {{/each}}

      <div class="overflow-scroll">
        <figure>
          {{#let (halfSize @model.mediaImage.original) as |halfSize|}}
            <img
              src={{@model.mediaImage.original.url}}
              alt={{@model.mediaImage.alt}}
              class="figure-img"
              width={{halfSize.width}}
              height={{halfSize.height}}
            />
          {{/let}}
          <figcaption class="figure-caption">original</figcaption>
        </figure>
      </div>
    </div>
  </template>
}
